import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { AlertTriangle, Home, Building2, Car, ShieldCheck, Lock } from "lucide-react";

const iconMap = { emergency: AlertTriangle, residential: Home, commercial: Building2, automotive: Car, safes: Lock, security: ShieldCheck };
const serviceImages = {
  emergency: "https://images.unsplash.com/photo-1671710552295-dd0d3fc76b59",
  residential: "https://images.unsplash.com/photo-1755288268839-4c24e1afb620",
  commercial: "https://images.unsplash.com/photo-1755288556425-f60755b10bff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzbWFydCUyMGRvb3IlMjBsb2NrfGVufDB8fHx8MTc3NjI1ODE1OXww&ixlib=rb-4.1.0&q=85",
  automotive: "https://images.unsplash.com/photo-1759452003649-552896c8ed63",
  safes: "https://images.unsplash.com/photo-1634224147987-95d2b7679fb0",
  security: null,
};
const serviceKeys = ["emergency", "residential", "commercial", "automotive", "safes", "security"];

// Bento grid layout classes for each card
const gridClasses = [
  "md:col-span-4 lg:col-span-5 lg:row-span-2 min-h-[320px]", // emergency - tall
  "md:col-span-4 lg:col-span-7 min-h-[200px]",                // residential - wide
  "md:col-span-4 lg:col-span-4 min-h-[200px]",                // commercial
  "md:col-span-4 lg:col-span-3 min-h-[200px]",                // automotive
  "md:col-span-4 lg:col-span-5 min-h-[200px]",                // safes - wide
  "md:col-span-8 lg:col-span-7 min-h-[180px]",                // security - full width
];

export default function ServicesSection() {
  const { t } = useLanguage();

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#0B0A08] noise-overlay">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
            {t.services.badge}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white mt-3 mb-4">
            {t.services.title}
          </h2>
          <p className="text-base text-[#8A8881] leading-relaxed max-w-xl">
            {t.services.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4 md:gap-5">
          {serviceKeys.map((key, i) => {
            const Icon = iconMap[key];
            const svc = t.services[key];
            const img = serviceImages[key];
            const hasImage = !!img;
            const isEmergency = key === "emergency";

            return (
              <motion.div
                key={key}
                data-testid={`service-card-${key}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`tracing-beam group relative rounded-2xl overflow-hidden border border-white/5 bg-[#141311] hover:border-[#D4AF37]/40 transition-all duration-500 ${gridClasses[i]}`}
              >
                {hasImage ? (
                  <>
                    <img src={img} alt={svc.title} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0A08] via-[#0B0A08]/50 to-transparent" />
                    <div className="relative z-10 flex flex-col justify-end h-full p-6 md:p-8">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isEmergency ? 'bg-red-900/60 border border-red-500/30' : 'bg-[#D4AF37]/10 border border-[#D4AF37]/20'}`}>
                          <Icon className={`w-5 h-5 ${isEmergency ? 'text-red-400' : 'text-[#D4AF37]'}`} />
                        </div>
                        <h3 className="font-heading text-xl font-semibold text-white">{svc.title}</h3>
                      </div>
                      <p className="text-sm text-[#D1D0CC] leading-relaxed">{svc.desc}</p>
                    </div>
                  </>
                ) : (
                  <div className="relative z-10 flex flex-col justify-end h-full p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-white">{svc.title}</h3>
                    </div>
                    <p className="text-sm text-[#8A8881] leading-relaxed">{svc.desc}</p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
